import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, EMPTY, finalize, map, Observable, of, tap } from 'rxjs';
import { ApiService } from '@app/core/api/api.service';
import { ApiError } from '@app/core/api/api.model';
import { LoginRequest, LoginResponse, User } from './auth.model';
import { TokenStore } from './token-store';

/**
 * Owns the current session: performs the login/logout API calls and exposes the
 * authenticated user as signals for the rest of the app.
 *
 * Endpoints are placeholders — point them at the real routes when known:
 *   POST auth/login   → LoginResponse
 *   POST auth/logout
 *   GET  auth/me      → User
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiService);
  private readonly tokens = inject(TokenStore);

  private readonly _user = signal<User | null>(null);
  private readonly _loading = signal(false);

  readonly user = this._user.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly isAuthenticated = computed(() => this.tokens.accessToken() !== null);

  login(credentials: LoginRequest): Observable<User> {
    this._loading.set(true);
    return this.api.post<LoginResponse>('auth/login', credentials).pipe(
      tap((response) => {
        this.tokens.set(response.accessToken, response.refreshToken);
        this._user.set(response.user);
      }),
      map((response) => response.user),
      finalize(() => this._loading.set(false)),
    );
  }

  logout(): Observable<void> {
    return this.api.post<void>('auth/logout').pipe(
      // best-effort — clear the client session regardless
      catchError(() => of(undefined)),
      tap(() => {
        this.tokens.clear();
        this._user.set(null);
      }),
    );
  }

  /**
   * Hydrate `user` from the stored token. Call once at startup (see
   * `provideAuthSession`). Silently clears the session on a 401.
   */
  loadSession(): Observable<void> {
    if (!this.tokens.accessToken()) return EMPTY;
    this._loading.set(true);
    return this.api.get<User>('auth/me').pipe(
      tap((user) => this._user.set(user)),
      map(() => undefined),
      catchError((error) => {
        if (error instanceof ApiError && error.status === 401) this.tokens.clear();
        return EMPTY;
      }),
      finalize(() => this._loading.set(false)),
    );
  }
}
